import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * announcement bar — marquee track
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 *
 * @extends {Component<Refs>}
 */
class AnnouncementMarquee extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.track.firstElementChild) return;

    this.#fillTrack();
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  #handleResize = debounce(() => this.#fillTrack(), 250);

  #fillTrack() {
    const { track } = this.refs;

    track.querySelectorAll('.announcement-bar__set[aria-hidden]').forEach((clone) => clone.remove());

    const unit = track.firstElementChild;
    if (!(unit instanceof HTMLElement)) return;

    const unitWidth = unit.getBoundingClientRect().width;
    if (unitWidth === 0) return;

    const viewportWidth = this.offsetWidth;
    const copies = Math.max(2, Math.ceil(viewportWidth / unitWidth) + 2);

    for (let i = 1; i < copies; i++) {
      track.appendChild(this.#clone(unit));
    }

    track.style.setProperty('--announcement-bar-copies', `${copies}`);
  }

  /**
   * @param {HTMLElement} unit
   */
  #clone(unit) {
    const clone = /** @type {HTMLElement} */ (unit.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a[href]').forEach((a) => {
      a.removeAttribute('href');
      a.setAttribute('tabindex', '-1');
    });

    [clone, ...clone.querySelectorAll('*')].forEach((el) => {
      [...el.attributes].forEach(({ name }) => {
        if (name === 'ref' || name === 'id' || name.startsWith('data-shopify')) {
          el.removeAttribute(name);
        }
      });
    });

    return clone;
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
